/**
 * RequireOrg - Route guard that enforces organization context.
 *
 * Two usage modes:
 * - With children: wraps content directly (e.g., <RequireOrg><Layout /></RequireOrg>)
 * - Without children: renders <Outlet /> for use as a pathless layout route
 *
 * If user has no org selected, redirects to /organizations.
 */

import type { ReactNode } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useCurrentOrg } from '../hooks/useCurrentOrg';
import { APP_ROUTES } from '../config/routes';

interface RequireOrgProps {
  children?: ReactNode;
}

export function RequireOrg({ children }: RequireOrgProps) {
  const { currentOrg, loading } = useCurrentOrg();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (!currentOrg) {
    return <Navigate to={APP_ROUTES.ORGANIZATIONS} replace />;
  }

  return children !== undefined ? <>{children}</> : <Outlet />;
}
